import { useState, createContext, useContext } from "react";
import "@/App.css";
import "@/Admin.css";
import { BrowserRouter, Routes, Route, Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { ChevronRight, MapPin, BedDouble, Bath, Square, Clock, Gavel, Menu, X, ArrowRight, Phone, Mail, Building2, Palette, Globe, CalendarDays } from "lucide-react";
import AdminPanel from "@/AdminPanel";

// Translations
const translations = {
  en: {
    nav: {
      realEstate: "Real Estate",
      fineArt: "Fine Art",
      events: "Events",
      about: "About",
      contact: "Contact"
    },
    hero: {
      subtitle: "Where Exceptional Meets Extraordinary",
      description: "The world's premier destination for distinguished real estate and museum-quality fine art",
      exploreEstates: "Explore Estates",
      viewFineArt: "View Fine Art",
      eventsBtn: "Events and Esclusive Travels"
    },
    featured: {
      estates: "Distinguished Estates",
      works: "Exceptional Works",
      viewAll: "View All"
    },
    cta: {
      title: "Begin Your Collection",
      description: "Our specialists are ready to assist with your acquisition or consignment",
      button: "Schedule a Consultation"
    },
    realEstatePage: {
      title: "Luxury Real Estate",
      subtitle: "Exceptional properties for discerning collectors",
      allProperties: "All Properties",
      mansions: "Mansions",
      penthouses: "Penthouses",
      villas: "Villas",
      estates: "Estates",
      townhouses: "Townhouses",
      beds: "Beds",
      baths: "Baths",
      auction: "Auction"
    },
    artPage: {
      title: "Fine Art",
      subtitle: "Museum-quality works from distinguished collections",
      allCategories: "All Categories",
      paintings: "Paintings",
      sculptures: "Sculptures",
      contemporary: "Contemporary",
      mixedMedia: "Mixed Media",
      photography: "Photography",
      estimate: "Estimate",
      currentBid: "Current Bid"
    },
    eventsPage: {
      title: "Events and Esclusivity",
      subtitle: "Corporate Events Organization and Travel",
      subtitle2: "Events and Luxury Travels",
      noEvents: "No events scheduled at the moment. Please check back soon.",
      learnMore: "Learn More"
    },
    detail: {
      guidePrice: "Guide Price",
      bedrooms: "Bedrooms",
      bathrooms: "Bathrooms",
      squareFeet: "Square Feet",
      aboutProperty: "About This Property",
      interested: "Interested in this property?",
      specialistsAvailable: "Our specialists are available to arrange a private viewing",
      requestInfo: "Request Information",
      propertyInquiry: "Property Inquiry",
      medium: "Medium",
      dimensions: "Dimensions",
      auctionDate: "Auction Date",
      aboutWork: "About This Work",
      requestCondition: "Request Condition Report",
      artInquiry: "Art Inquiry"
    },
    form: {
      yourName: "Your Name",
      email: "Email Address",
      phone: "Phone Number",
      message: "Your Message",
      howCanWeAssist: "How can we assist you?",
      submit: "Submit Inquiry",
      sendMessage: "Send Message"
    },
    about: {
      title: "About Montgomery's",
      tagline: "A Legacy of Excellence Since 1892",
      heritage: "Our Heritage",
      heritageText: "For over a century, Montgomery's has been the trusted name in fine art and luxury real estate. Founded in London in 1892 by Sir Edward Montgomery, our house has grown to become one of the world's most respected auction establishments, with galleries in New York, London, Hong Kong, and Geneva.",
      expertise: "Our Expertise",
      luxuryRealEstate: "Luxury Real Estate",
      luxuryRealEstateText: "Our real estate division specializes in extraordinary properties worldwide, from historic estates to contemporary architectural masterpieces.",
      fineArtTitle: "Fine Art",
      fineArtText: "Our specialists curate exceptional works spanning Old Masters to Contemporary art, with deep expertise in provenance research and authentication.",
      commitment: "Our Commitment",
      commitmentText: "Every transaction at Montgomery's is guided by our founding principles: integrity, expertise, and exceptional service. Whether you are a seasoned collector or discovering your first masterpiece, our team is dedicated to ensuring a seamless experience."
    },
    contact: {
      title: "Contact Us",
      subtitle: "Our specialists are available to assist you",
      getInTouch: "Get in Touch",
      sendMessage: "Send a Message"
    },
    footer: {
      tagline: "Curating Excellence Since 1892",
      departments: "Departments",
      company: "Company",
      copyright: "© 2025 Montgomery's Auction House. All rights reserved."
    },
    toast: {
      inquirySuccess: "Inquiry submitted successfully",
      inquiryError: "Failed to submit inquiry",
      messageSuccess: "Message sent successfully",
      messageError: "Failed to send message"
    }
  },
  it: {
    nav: {
      realEstate: "Immobili",
      fineArt: "Belle Arti",
      events: "Eventi",
      about: "Chi Siamo",
      contact: "Contatti"
    },
    hero: {
      subtitle: "Dove l'Eccezionale Incontra lo Straordinario",
      description: "La destinazione premier mondiale per immobili di prestigio e opere d'arte di qualità museale",
      exploreEstates: "Esplora le Proprietà",
      viewFineArt: "Scopri le Opere",
      eventsBtn: "Eventi e Viaggi Esclusivi"
    },
    featured: {
      estates: "Proprietà di Prestigio",
      works: "Opere Eccezionali",
      viewAll: "Vedi Tutto"
    },
    cta: {
      title: "Inizia la Tua Collezione",
      description: "I nostri specialisti sono pronti ad assisterti nell'acquisizione o consegna",
      button: "Prenota una Consulenza"
    },
    realEstatePage: {
      title: "Immobili di Lusso",
      subtitle: "Proprietà eccezionali per collezionisti esigenti",
      allProperties: "Tutte le Proprietà",
      mansions: "Ville",
      penthouses: "Attici",
      villas: "Ville al Mare",
      estates: "Tenute",
      townhouses: "Case a Schiera",
      beds: "Camere",
      baths: "Bagni",
      auction: "Asta"
    },
    artPage: {
      title: "Belle Arti",
      subtitle: "Opere di qualità museale da collezioni prestigiose",
      allCategories: "Tutte le Categorie",
      paintings: "Dipinti",
      sculptures: "Sculture",
      contemporary: "Arte Contemporanea",
      mixedMedia: "Tecnica Mista",
      photography: "Fotografia",
      estimate: "Stima",
      currentBid: "Offerta Attuale"
    },
    eventsPage: {
      title: "Eventi ed Esclusività",
      subtitle: "Organizzazione Eventi Aziendali e Viaggi",
      subtitle2: "Eventi e Viaggi di Lusso",
      noEvents: "Nessun evento in programma al momento. Torna a trovarci presto.",
      learnMore: "Scopri di Più"
    },
    detail: {
      guidePrice: "Prezzo Guida",
      bedrooms: "Camere da Letto",
      bathrooms: "Bagni",
      squareFeet: "Metri Quadrati",
      aboutProperty: "Informazioni sulla Proprietà",
      interested: "Interessato a questa proprietà?",
      specialistsAvailable: "I nostri specialisti sono disponibili per organizzare una visita privata",
      requestInfo: "Richiedi Informazioni",
      propertyInquiry: "Richiesta Proprietà",
      medium: "Tecnica",
      dimensions: "Dimensioni",
      auctionDate: "Data dell'Asta",
      aboutWork: "Informazioni sull'Opera",
      requestCondition: "Richiedi Rapporto di Condizione",
      artInquiry: "Richiesta Opera d'Arte"
    },
    form: {
      yourName: "Il Tuo Nome",
      email: "Indirizzo Email",
      phone: "Numero di Telefono",
      message: "Il Tuo Messaggio",
      howCanWeAssist: "Come possiamo assisterti?",
      submit: "Invia Richiesta",
      sendMessage: "Invia Messaggio"
    },
    about: {
      title: "Chi è Montgomery's",
      tagline: "Un'Eredità di Eccellenza dal 1892",
      heritage: "La Nostra Storia",
      heritageText: "Per oltre un secolo, Montgomery's è stato il nome di fiducia nel mondo delle belle arti e degli immobili di lusso. Fondata a Londra nel 1892 da Sir Edward Montgomery, la nostra casa è cresciuta fino a diventare una delle più rispettate case d'asta al mondo, con gallerie a New York, Londra, Hong Kong e Ginevra.",
      expertise: "La Nostra Competenza",
      luxuryRealEstate: "Immobili di Lusso",
      luxuryRealEstateText: "La nostra divisione immobiliare è specializzata in proprietà straordinarie in tutto il mondo, dalle tenute storiche ai capolavori architettonici contemporanei.",
      fineArtTitle: "Belle Arti",
      fineArtText: "I nostri specialisti curano opere eccezionali che spaziano dai Grandi Maestri all'arte contemporanea, con profonda competenza nella ricerca della provenienza e nell'autenticazione.",
      commitment: "Il Nostro Impegno",
      commitmentText: "Ogni transazione presso Montgomery's è guidata dai nostri principi fondanti: integrità, competenza e servizio eccezionale. Che tu sia un collezionista esperto o stia scoprendo il tuo primo capolavoro, il nostro team è dedicato a garantirti un'esperienza impeccabile."
    },
    contact: {
      title: "Contattaci",
      subtitle: "I nostri specialisti sono disponibili per assisterti",
      getInTouch: "Mettiti in Contatto",
      sendMessage: "Invia un Messaggio"
    },
    footer: {
      tagline: "Curatori di Eccellenza dal 1892",
      departments: "Dipartimenti",
      company: "Azienda",
      copyright: "© 2025 Montgomery's Casa d'Aste. Tutti i diritti riservati."
    },
    toast: {
      inquirySuccess: "Richiesta inviata con successo",
      inquiryError: "Invio richiesta fallito",
      messageSuccess: "Messaggio inviato con successo",
      messageError: "Invio messaggio fallito"
    }
  }
};

// Language Context
const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('montgomery-lang') || 'en';
  });

  const t = translations[language];

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('montgomery-lang', lang);
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Site Settings Context
const SiteSettingsContext = createContext();

export const useSiteSettings = () => {
  const context = useContext(SiteSettingsContext);
  return context || {};
};

const SiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSettings(response.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  return (
    <SiteSettingsContext.Provider value={settings}>
      {children}
    </SiteSettingsContext.Provider>
  );
};

// Format currency - now supports EUR
const formatCurrency = (amount, currency = 'EUR') => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return 'TBA';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

// Language Switcher Component
const LanguageSwitcher = () => {
  const { language, changeLanguage } = useLanguage();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="lang-switcher" data-testid="language-switcher">
          <Globe size={18} />
          <span>{language === 'en' ? 'EN' : 'IT'}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="lang-menu">
        <DropdownMenuItem 
          onClick={() => changeLanguage('en')}
          className={language === 'en' ? 'lang-active' : ''}
          data-testid="lang-en"
        >
          English
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => changeLanguage('it')}
          className={language === 'it' ? 'lang-active' : ''}
          data-testid="lang-it"
        >
          Italiano
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useLanguage();
  const settings = useSiteSettings();

  return (
    <nav className="nav-container" data-testid="navigation">
      <div className="nav-content">
        <Link to="/" className="nav-logo" data-testid="nav-logo">
          <span className="logo-text">{settings?.company_name || "Montgomery's"}</span>
          <span className="logo-subtitle">FINE ART & ESTATES</span>
        </Link>

        <div className="nav-links-desktop">
          <Link to="/real-estate" className="nav-link" data-testid="nav-real-estate">{t.nav.realEstate}</Link>
          <Link to="/art" className="nav-link" data-testid="nav-art">{t.nav.fineArt}</Link>
          <Link to="/events" className="nav-link" data-testid="nav-events">{t.nav.events}</Link>
          <Link to="/about" className="nav-link" data-testid="nav-about">{t.nav.about}</Link>
          <Link to="/contact" className="nav-link" data-testid="nav-contact">{t.nav.contact}</Link>
          <LanguageSwitcher />
        </div>

        <div className="nav-mobile-actions">
          <LanguageSwitcher />
          <button className="nav-menu-btn" onClick={() => setIsOpen(!isOpen)} data-testid="nav-menu-toggle">
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="nav-mobile-menu" data-testid="nav-mobile-menu">
          <Link to="/real-estate" className="nav-mobile-link" onClick={() => setIsOpen(false)}>{t.nav.realEstate}</Link>
          <Link to="/art" className="nav-mobile-link" onClick={() => setIsOpen(false)}>{t.nav.fineArt}</Link>
          <Link to="/events" className="nav-mobile-link" onClick={() => setIsOpen(false)}>{t.nav.events}</Link>
          <Link to="/about" className="nav-mobile-link" onClick={() => setIsOpen(false)}>{t.nav.about}</Link>
          <Link to="/contact" className="nav-mobile-link" onClick={() => setIsOpen(false)}>{t.nav.contact}</Link>
        </div>
      )}
    </nav>
  );
};

// Footer Component
const Footer = () => {
  const { t } = useLanguage();
  const settings = useSiteSettings();
  
  return (
    <footer className="footer" data-testid="footer">
      <div className="footer-content">
        <div className="footer-brand">
          <h3 className="footer-logo">{settings?.company_name || "Montgomery's"}</h3>
          <p className="footer-tagline">{settings?.footer_tagline || t.footer.tagline}</p>
        </div>
        <div className="footer-links">
          <div className="footer-column">
            <h4>{t.footer.departments}</h4>
            <Link to="/real-estate">{t.nav.realEstate}</Link>
            <Link to="/art">{t.nav.fineArt}</Link>
            <Link to="/events">{t.nav.events}</Link>
          </div>
          <div className="footer-column">
            <h4>{t.footer.company}</h4>
            <Link to="/about">{t.nav.about}</Link>
            <Link to="/contact">{t.nav.contact}</Link>
          </div>
        </div>
      </div>
      <Separator className="footer-separator" />
      <div className="footer-bottom">
        <p className="footer-inquiry">Contact for inquiries: +393513052093</p>
        <p className="footer-copyright">© 2025 {settings?.company_name || "Montgomery's"}. All rights reserved.</p>
        <p className="footer-brand-mark">Montgomery's</p>
      </div>
    </footer>
  );
};

// Home Page
const Home = () => {
  const [featured, setFeatured] = useState({ real_estate: [], art: [] });
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();
  const settings = useSiteSettings();

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const response = await axios.get(`${API}/featured`);
        setFeatured(response.data);
      } catch (error) {
        console.error('Error fetching featured:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFeatured();
  }, []);

  const heroStyle = settings?.hero_image_url ? {
    backgroundImage: `url('${settings.hero_image_url}')`
  } : {};

  return (
    <div className="home-page" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero" style={heroStyle} data-testid="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title">{settings?.company_name || "Montgomery's"}</h1>
          <p className="hero-subtitle">{settings?.tagline || t.hero.subtitle}</p>
          <p className="hero-description">{settings?.description || t.hero.description}</p>
          <div className="hero-buttons">
            <Link to="/real-estate">
              <Button className="hero-btn primary" data-testid="hero-btn-real-estate">
                <Building2 size={18} />
                {t.hero.exploreEstates}
              </Button>
            </Link>
            <Link to="/art">
              <Button className="hero-btn secondary" data-testid="hero-btn-art">
                <Palette size={18} />
                {t.nav.fineArt}
              </Button>
            </Link>
            <Link to="/events">
              <Button className="hero-btn secondary" data-testid="hero-btn-events">
                <CalendarDays size={18} />
                {t.hero.eventsBtn}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Real Estate */}
      <section className="featured-section" data-testid="featured-real-estate-section">
        <div className="section-header">
          <h2 className="section-title">{t.featured.estates}</h2>
          <Link to="/real-estate" className="section-link">
            {t.featured.viewAll} <ChevronRight size={18} />
          </Link>
        </div>
        <div className="featured-grid">
          {featured.real_estate.map((item) => (
            <Link to={`/real-estate/${item.id}`} key={item.id} className="featured-card-link">
              <Card className="featured-card" data-testid={`featured-re-${item.id}`}>
                <div className="card-image-container">
                  <img src={item.image_url} alt={item.title} className="card-image" />
                  <Badge className="card-badge">{item.property_type}</Badge>
                </div>
                <CardContent className="card-content">
                  <h3 className="card-title">{item.title}</h3>
                  <p className="card-location">
                    <MapPin size={14} /> {item.location}
                  </p>
                  <p className="card-price">{formatCurrency(item.price)}</p>
                  <div className="card-features">
                    <span><BedDouble size={14} /> {item.bedrooms}</span>
                    <span><Bath size={14} /> {item.bathrooms}</span>
                    <span><Square size={14} /> {item.sqft.toLocaleString()} m²</span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured Art */}
      <section className="featured-section art-section" data-testid="featured-art-section">
        <div className="section-header">
          <h2 className="section-title">{t.featured.works}</h2>
          <Link to="/art" className="section-link">
            {t.featured.viewAll} <ChevronRight size={18} />
          </Link>
        </div>
        <div className="featured-grid">
          {featured.art.map((item) => (
            <Link to={`/art/${item.id}`} key={item.id} className="featured-card-link">
              <Card className="featured-card" data-testid={`featured-art-${item.id}`}>
                <div className="card-image-container art">
                  <img src={item.image_url} alt={item.title} className="card-image" />
                  <Badge className="card-badge">{item.category}</Badge>
                </div>
                <CardContent className="card-content">
                  <h3 className="card-title">{item.title}</h3>
                  <p className="card-artist">{item.artist}, {item.year}</p>
                  <p className="card-estimate">
                    {t.artPage.estimate}: {formatCurrency(item.estimated_value_low)} – {formatCurrency(item.estimated_value_high)}
                  </p>
                  {item.current_bid && (
                    <p className="card-bid">
                      <Gavel size={14} /> {t.artPage.currentBid}: {formatCurrency(item.current_bid)}
                    </p>
                  )}
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section" data-testid="cta-section">
        <div className="cta-content">
          <h2>{t.cta.title}</h2>
          <p>{t.cta.description}</p>
          <Link to="/contact">
            <Button className="cta-btn" data-testid="cta-contact-btn">
              {t.cta.button} <ArrowRight size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

// Real Estate Listing Page
const RealEstatePage = () => {
  const [listings, setListings] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    const fetchListings = async () => {
      try {
        let url = `${API}/real-estate`;
        if (filter !== 'all') {
          url += `?property_type=${filter}`;
        }
        const response = await axios.get(url);
        setListings(response.data);
      } catch (error) {
        console.error('Error fetching listings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchListings();
  }, [filter]);

  return (
    <div className="listing-page" data-testid="real-estate-page">
      <div className="page-header">
        <h1>{t.realEstatePage.title}</h1>
        <p>{t.realEstatePage.subtitle}</p>
      </div>

      <div className="filter-bar">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="filter-select" data-testid="property-type-filter">
            <SelectValue placeholder={t.realEstatePage.allProperties} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.realEstatePage.allProperties}</SelectItem>
            <SelectItem value="mansion">{t.realEstatePage.mansions}</SelectItem>
            <SelectItem value="penthouse">{t.realEstatePage.penthouses}</SelectItem>
            <SelectItem value="villa">{t.realEstatePage.villas}</SelectItem>
            <SelectItem value="estate">{t.realEstatePage.estates}</SelectItem>
            <SelectItem value="townhouse">{t.realEstatePage.townhouses}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="listings-grid" data-testid="listings-grid">
        {listings.map((item) => (
          <Link to={`/real-estate/${item.id}`} key={item.id} className="listing-card-link">
            <Card className="listing-card" data-testid={`listing-${item.id}`}>
              <div className="listing-image-container">
                <img src={item.image_url} alt={item.title} className="listing-image" />
                <Badge className="listing-badge">{item.property_type}</Badge>
                {item.auction_end_date && (
                  <div className="auction-date">
                    <Clock size={12} /> {t.realEstatePage.auction}: {formatDate(item.auction_end_date)}
                  </div>
                )}
              </div>
              <CardContent className="listing-content">
                <h3 className="listing-title">{item.title}</h3>
                <p className="listing-location">
                  <MapPin size={14} /> {item.location}
                </p>
                <p className="listing-price">{formatCurrency(item.price)}</p>
                <div className="listing-features">
                  <span><BedDouble size={14} /> {item.bedrooms} {t.realEstatePage.beds}</span>
                  <span><Bath size={14} /> {item.bathrooms} {t.realEstatePage.baths}</span>
                  <span><Square size={14} /> {item.sqft.toLocaleString()} sqft</span>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
};

// Art Listing Page
const ArtPage = () => {
  const [listings, setListings] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    const fetchListings = async () => {
      try {
        let url = `${API}/art`;
        if (filter !== 'all') {
          url += `?category=${filter}`;
        }
        const response = await axios.get(url);
        setListings(response.data);
      } catch (error) {
        console.error('Error fetching listings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchListings();
  }, [filter]);

  return (
    <div className="listing-page art-page" data-testid="art-page">
      <div className="page-header">
        <h1>{t.artPage.title}</h1>
        <p>{t.artPage.subtitle}</p>
      </div>

      <div className="filter-bar">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="filter-select" data-testid="art-category-filter">
            <SelectValue placeholder={t.artPage.allCategories} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.artPage.allCategories}</SelectItem>
            <SelectItem value="painting">{t.artPage.paintings}</SelectItem>
            <SelectItem value="sculpture">{t.artPage.sculptures}</SelectItem>
            <SelectItem value="contemporary">{t.artPage.contemporary}</SelectItem>
            <SelectItem value="mixed_media">{t.artPage.mixedMedia}</SelectItem>
            <SelectItem value="photography">{t.artPage.photography}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="listings-grid art-grid" data-testid="art-listings-grid">
        {listings.map((item) => (
          <Link to={`/art/${item.id}`} key={item.id} className="listing-card-link">
            <Card className="listing-card art-card" data-testid={`art-listing-${item.id}`}>
              <div className="listing-image-container art">
                <img src={item.image_url} alt={item.title} className="listing-image" />
                <Badge className="listing-badge">{item.category}</Badge>
              </div>
              <CardContent className="listing-content">
                <h3 className="listing-title">{item.title}</h3>
                <p className="listing-artist">{item.artist}{item.year && `, ${item.year}`}</p>
                <p className="listing-medium">{item.medium}</p>
                <p className="listing-estimate">
                  {t.artPage.estimate}: {formatCurrency(item.estimated_value_low)} – {formatCurrency(item.estimated_value_high)}
                </p>
                {item.current_bid && (
                  <p className="listing-bid">
                    <Gavel size={14} /> {t.artPage.currentBid}: {formatCurrency(item.current_bid)}
                  </p>
                )}
                {item.auction_end_date && (
                  <p className="listing-auction">
                    <Clock size={14} /> {formatDate(item.auction_end_date)}
                  </p>
                )}
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
};

// Real Estate Detail Page
const RealEstateDetail = () => {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const { t } = useLanguage();

  useEffect(() => {
    const fetchListing = async () => {
      try {
        const response = await axios.get(`${API}/real-estate/${id}`);
        setListing(response.data);
      } catch (error) {
        console.error('Error fetching listing:', error);
      }
    };
    fetchListing();
  }, [id]);

  const handleInquiry = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/inquiries`, {
        ...formData,
        listing_id: id,
        listing_type: 'real_estate'
      });
      toast.success(t.toast.inquirySuccess);
      setInquiryOpen(false);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error(t.toast.inquiryError);
    }
  };

  if (!listing) return <div className="loading">Loading...</div>;

  return (
    <div className="detail-page" data-testid="real-estate-detail">
      <div className="detail-hero">
        <img src={listing.image_url} alt={listing.title} className="detail-image" />
        <div className="detail-hero-overlay"></div>
        <div className="detail-hero-content">
          <Badge className="detail-badge">{listing.property_type}</Badge>
          <h1 className="detail-title">{listing.title}</h1>
          <p className="detail-location"><MapPin size={18} /> {listing.location}</p>
        </div>
      </div>

      <div className="detail-content">
        <div className="detail-main">
          <div className="detail-price-bar">
            <div className="price-info">
              <span className="price-label">{t.detail.guidePrice}</span>
              <span className="price-value">{formatCurrency(listing.price)}</span>
            </div>
            {listing.auction_end_date && (
              <div className="auction-info">
                <Clock size={16} />
                <span>{t.realEstatePage.auction}: {formatDate(listing.auction_end_date)}</span>
              </div>
            )}
          </div>

          <div className="detail-features">
            <div className="feature-item">
              <BedDouble size={24} />
              <span className="feature-value">{listing.bedrooms}</span>
              <span className="feature-label">{t.detail.bedrooms}</span>
            </div>
            <div className="feature-item">
              <Bath size={24} />
              <span className="feature-value">{listing.bathrooms}</span>
              <span className="feature-label">{t.detail.bathrooms}</span>
            </div>
            <div className="feature-item">
              <Square size={24} />
              <span className="feature-value">{listing.sqft.toLocaleString()}</span>
              <span className="feature-label">{t.detail.squareFeet}</span>
            </div>
          </div>

          <Separator />

          <div className="detail-description">
            <h2>{t.detail.aboutProperty}</h2>
            <p>{listing.description}</p>
          </div>
        </div>

        <div className="detail-sidebar">
          <Card className="inquiry-card">
            <CardContent className="inquiry-content">
              <h3>{t.detail.interested}</h3>
              <p>{t.detail.specialistsAvailable}</p>
              <Dialog open={inquiryOpen} onOpenChange={setInquiryOpen}>
                <DialogTrigger asChild>
                  <Button className="inquiry-btn" data-testid="inquire-btn">
                    {t.detail.requestInfo}
                  </Button>
                </DialogTrigger>
                <DialogContent className="inquiry-dialog">
                  <DialogHeader>
                    <DialogTitle>{t.detail.propertyInquiry}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleInquiry} className="inquiry-form">
                    <Input
                      placeholder={t.form.yourName}
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      data-testid="inquiry-name"
                    />
                    <Input
                      type="email"
                      placeholder={t.form.email}
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      data-testid="inquiry-email"
                    />
                    <Input
                      placeholder={t.form.phone}
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      data-testid="inquiry-phone"
                    />
                    <Textarea
                      placeholder={t.form.message}
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      required
                      data-testid="inquiry-message"
                    />
                    <Button type="submit" className="submit-btn" data-testid="submit-inquiry">{t.form.submit}</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Art Detail Page
const ArtDetail = () => {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const { t } = useLanguage();

  useEffect(() => {
    const fetchListing = async () => {
      try {
        const response = await axios.get(`${API}/art/${id}`);
        setListing(response.data);
      } catch (error) {
        console.error('Error fetching listing:', error);
      }
    };
    fetchListing();
  }, [id]);

  const handleInquiry = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/inquiries`, {
        ...formData,
        listing_id: id,
        listing_type: 'art'
      });
      toast.success(t.toast.inquirySuccess);
      setInquiryOpen(false);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error(t.toast.inquiryError);
    }
  };

  if (!listing) return <div className="loading">Loading...</div>;

  return (
    <div className="detail-page art-detail" data-testid="art-detail">
      <div className="art-detail-container">
        <div className="art-image-section">
          <img src={listing.image_url} alt={listing.title} className="art-detail-image" />
        </div>

        <div className="art-info-section">
          <Badge className="detail-badge">{listing.category}</Badge>
          <h1 className="art-detail-title">{listing.title}</h1>
          <p className="art-detail-artist">{listing.artist}{listing.year && `, ${listing.year}`}</p>

          <Separator className="art-separator" />

          <div className="art-details">
            <div className="art-detail-row">
              <span className="detail-label">{t.detail.medium}</span>
              <span className="detail-value">{listing.medium}</span>
            </div>
            {listing.dimensions && (
              <div className="art-detail-row">
                <span className="detail-label">{t.detail.dimensions}</span>
                <span className="detail-value">{listing.dimensions}</span>
              </div>
            )}
            <div className="art-detail-row">
              <span className="detail-label">{t.artPage.estimate}</span>
              <span className="detail-value">
                {formatCurrency(listing.estimated_value_low)} – {formatCurrency(listing.estimated_value_high)}
              </span>
            </div>
            {listing.current_bid && (
              <div className="art-detail-row current-bid">
                <span className="detail-label"><Gavel size={16} /> {t.artPage.currentBid}</span>
                <span className="detail-value bid-value">{formatCurrency(listing.current_bid)}</span>
              </div>
            )}
            {listing.auction_end_date && (
              <div className="art-detail-row">
                <span className="detail-label"><Clock size={16} /> {t.detail.auctionDate}</span>
                <span className="detail-value">{formatDate(listing.auction_end_date)}</span>
              </div>
            )}
          </div>

          <Separator className="art-separator" />

          <div className="art-description">
            <h3>{t.detail.aboutWork}</h3>
            <p>{listing.description}</p>
          </div>

          <Dialog open={inquiryOpen} onOpenChange={setInquiryOpen}>
            <DialogTrigger asChild>
              <Button className="art-inquiry-btn" data-testid="art-inquire-btn">
                {t.detail.requestCondition}
              </Button>
            </DialogTrigger>
            <DialogContent className="inquiry-dialog">
              <DialogHeader>
                <DialogTitle>{t.detail.artInquiry}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleInquiry} className="inquiry-form">
                <Input
                  placeholder={t.form.yourName}
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  data-testid="art-inquiry-name"
                />
                <Input
                  type="email"
                  placeholder={t.form.email}
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  data-testid="art-inquiry-email"
                />
                <Input
                  placeholder={t.form.phone}
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  data-testid="art-inquiry-phone"
                />
                <Textarea
                  placeholder={t.form.message}
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  required
                  data-testid="art-inquiry-message"
                />
                <Button type="submit" className="submit-btn" data-testid="submit-art-inquiry">{t.form.submit}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

// About Page
const AboutPage = () => {
  const { t } = useLanguage();
  const settings = useSiteSettings();
  
  return (
    <div className="about-page" data-testid="about-page">
      <div className="about-hero">
        <h1>About {settings?.company_name || "Montgomery's"}</h1>
        <p className="about-tagline">{settings?.footer_tagline || t.about.tagline}</p>
      </div>

      <div className="about-content">
        <section className="about-section">
          <h2>{t.about.heritage}</h2>
          <p>{settings?.about_heritage || t.about.heritageText}</p>
        </section>

        <section className="about-section">
          <h2>{t.about.expertise}</h2>
          <div className="expertise-grid">
            <div className="expertise-card">
              <Building2 size={40} />
              <h3>{t.about.luxuryRealEstate}</h3>
              <p>{t.about.luxuryRealEstateText}</p>
            </div>
            <div className="expertise-card">
              <MapPin size={40} />
              <h3>Investment Properties</h3>
              <p>We specialize in properties with significant investment potential, from historic restorations to modern developments in prime Italian locations.</p>
            </div>
          </div>
        </section>

        <section className="about-section">
          <h2>{t.about.commitment}</h2>
          <p>{settings?.about_commitment || t.about.commitmentText}</p>
        </section>
      </div>
    </div>
  );
};

// Contact Page
const ContactPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const { t } = useLanguage();
  const settings = useSiteSettings();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/inquiries`, formData);
      toast.success(t.toast.messageSuccess);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error(t.toast.messageError);
    }
  };

  return (
    <div className="contact-page" data-testid="contact-page">
      <div className="contact-hero">
        <h1>{t.contact.title}</h1>
        <p>{t.contact.subtitle}</p>
      </div>

      <div className="contact-content">
        <div className="contact-info">
          <h2>{t.contact.getInTouch}</h2>
          <div className="contact-details">
            <div className="contact-item">
              <Phone size={20} />
              <div>
                <h4>Phone</h4>
                <p>{settings?.phone || "+39 335 549 5609"}</p>
              </div>
            </div>
            <div className="contact-item">
              <Mail size={20} />
              <div>
                <h4>Email</h4>
                <p>{settings?.email || "direction@jetimmagine.com"}</p>
              </div>
            </div>
            <div className="contact-item">
              <MapPin size={20} />
              <div>
                <h4>Italia</h4>
                <p>{settings?.address || "Via Enzo Ferrari 1"}<br />{settings?.city || "Lido di Jesolo, 30016, Venezia"}</p>
              </div>
            </div>
          </div>
        </div>

        <Card className="contact-form-card">
          <CardContent>
            <h2>{t.contact.sendMessage}</h2>
            <form onSubmit={handleSubmit} className="contact-form">
              <Input
                placeholder={t.form.yourName}
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                data-testid="contact-name"
              />
              <Input
                type="email"
                placeholder={t.form.email}
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                data-testid="contact-email"
              />
              <Input
                placeholder={t.form.phone}
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                data-testid="contact-phone"
              />
              <Textarea
                placeholder={t.form.howCanWeAssist}
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                required
                rows={5}
                data-testid="contact-message"
              />
              <Button type="submit" className="contact-submit" data-testid="contact-submit">{t.form.sendMessage}</Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Events Page
const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/events`);
        setEvents(response.data);
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  return (
    <div className="listing-page events-page" data-testid="events-page">
      <div className="page-header">
        <h1>{t.eventsPage.title}</h1>
        <p>{t.eventsPage.subtitle}</p>
        <p className="page-subtitle-secondary">{t.eventsPage.subtitle2}</p>
      </div>

      {!loading && events.length === 0 ? (
        <p className="no-events" data-testid="no-events">{t.eventsPage.noEvents}</p>
      ) : (
        <div className="listings-grid events-grid" data-testid="events-grid">
          {events.map((item) => (
            <Card className="listing-card event-card" key={item.id} data-testid={`event-${item.id}`}>
              <div className="listing-image-container">
                <img src={item.image_url} alt={item.title} className="listing-image" />
                {item.featured && <Badge className="listing-badge">Featured</Badge>}
              </div>
              <CardContent className="listing-content">
                <h3 className="listing-title">{item.title}</h3>
                {item.event_date && (
                  <p className="listing-location">
                    <CalendarDays size={14} /> {formatDate(item.event_date)}
                  </p>
                )}
                <p className="listing-location">
                  <MapPin size={14} /> {item.location}
                </p>
                <p className="event-description">{item.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <SiteSettingsProvider>
        <div className="App">
          <BrowserRouter>
            <Routes>
              <Route path="/admin" element={<AdminPanel />} />
              <Route path="/*" element={
                <>
                  <Navigation />
                  <main className="main-content">
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/real-estate" element={<RealEstatePage />} />
                      <Route path="/real-estate/:id" element={<RealEstateDetail />} />
                      <Route path="/art" element={<ArtPage />} />
                      <Route path="/art/:id" element={<ArtDetail />} />
                      <Route path="/events" element={<EventsPage />} />
                      <Route path="/about" element={<AboutPage />} />
                      <Route path="/contact" element={<ContactPage />} />
                    </Routes>
                  </main>
                  <Footer />
                </>
              } />
            </Routes>
            <Toaster position="bottom-right" richColors />
          </BrowserRouter>
        </div>
      </SiteSettingsProvider>
    </LanguageProvider>
  );
}

export default App;
